import { useCallback, useImperativeHandle, useMemo, useRef } from 'react'

import { Separator, Stack } from '@forto-dev/phoenix-ui-core'

import { useLocalisation } from '@/utils/localisation'

import { AddressForm, AddressFormField } from './AddressForm'
import { useAddressSelectorContext } from '../contexts/useAddressSelectorContext'
import { useAddressSelectorDispatchContext } from '../contexts/useAddressSelectorDispatchContext'
import { AddressData, isLocalLanguageCountryRequired } from '../schema'

const manualAddressFormFields: Record<keyof AddressData, boolean> = {
  countryCode: true,
  route1: true,
  route2: false,
  route3: false,
  route4: false,
  region1: true,
  region2: false,
  region3: false,
  region4: false,
  locality1: true,
  locality2: false,
  locality3: false,
  locality4: false,
  postalCode: true,
  additionalInformation: false,
  companyName: false,
  locationRef: false,
  source: false,
}

const enabledManualAddressFormFields = Object.keys(manualAddressFormFields).filter(
  (key) => manualAddressFormFields[key as keyof AddressData]
) as Array<keyof AddressData>

export const ManualAddress = () => {
  const { value: addressSelectorValue, inputRef, error, warning } = useAddressSelectorContext()
  const { onManualAddressSelected } = useAddressSelectorDispatchContext()
  const addressFormRef = useRef<{ focusCountrySelector: () => void; focusStreetField: () => void }>(null)
  const localAddressFormRef = useRef<{ focusCountrySelector: () => void; focusStreetField: () => void }>(null)
  const { translate } = useLocalisation()

  const { address, localAddress } = useMemo(() => {
    return {
      address: addressSelectorValue?.address ?? { countryCode: '', route1: '' },
      localAddress: addressSelectorValue?.localAddress ?? { countryCode: '', route1: '' },
    }
  }, [addressSelectorValue])

  const localAddressRequiredFields = useMemo(() => {
    if (isLocalLanguageCountryRequired(localAddress)) {
      return ['countryCode'] as Array<AddressFormField>
    }
  }, [localAddress])

  const { addressError, localAddressError } = useMemo(() => {
    const errorData = error as unknown as Record<string, Record<AddressFormField, { message: string }>>
    return {
      addressError: errorData?.address,
      localAddressError: errorData?.localAddress,
    }
  }, [error])

  const addressWarning = warning
    ? ({ countryCode: { message: warning } } as Record<AddressFormField, { message: string }>)
    : undefined

  useImperativeHandle(inputRef, () => {
    return {
      focus() {
        if (addressError?.countryCode) {
          addressFormRef.current?.focusCountrySelector()
        } else if (addressError?.route1) {
          addressFormRef.current?.focusStreetField()
        } else if (localAddressError?.countryCode) {
          localAddressFormRef.current?.focusCountrySelector()
        } else if (localAddressError?.route1) {
          localAddressFormRef.current?.focusStreetField()
        }
      },
    }
  }, [addressError, localAddressError])

  const onAddressChange = useCallback(
    (field: AddressFormField, value: string) => {
      onManualAddressSelected({
        ...(addressSelectorValue ?? {
          address: {},
          addressFormatted: null,
          localAddress: null,
          localAddressFormatted: null,
        }),
        address: { ...address, [field]: !value ? undefined : value },
      })
    },
    [address, addressSelectorValue, onManualAddressSelected]
  )

  const onLocalAddressChange = useCallback(
    (field: AddressFormField, value: string) => {
      const updatedLocalAddress = localAddress ? { ...localAddress, [field]: value } : { [field]: value }

      const isFormEmpty =
        !value && enabledManualAddressFormFields.every((key) => !updatedLocalAddress[key as keyof AddressData])

      onManualAddressSelected({
        ...(addressSelectorValue ?? {
          address: {},
          addressFormatted: null,
          localAddress: null,
          localAddressFormatted: null,
        }),
        localAddress: isFormEmpty ? null : updatedLocalAddress,
      })
    },
    [localAddress, addressSelectorValue, onManualAddressSelected]
  )

  return (
    <Stack aria-label={translate('locations.search_address_form.manual_section')} gap={16}>
      <AddressForm
        value={address}
        error={addressError}
        onChange={onAddressChange}
        title={translate('locations.search_address_form.manual_default_header')}
        ref={addressFormRef}
        requiredFields={['countryCode', 'route1']}
        aria-label={translate('locations.search_address_form.manual_default_section')}
        warning={addressWarning}
      />
      <Separator orientation="horizontal" />
      <AddressForm
        value={localAddress}
        error={localAddressError}
        onChange={onLocalAddressChange}
        title={translate('locations.search_address_form.manual_local_header')}
        ref={localAddressFormRef}
        aria-label={translate('locations.search_address_form.manual_local_section')}
        requiredFields={localAddressRequiredFields}
      />
    </Stack>
  )
}
